import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../lib/api';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Play, History, Settings, Target, Shield, Globe, Zap, Award,
  AlertTriangle, CheckCircle, Clock, ArrowUpRight, ChevronRight, Loader2,
  Calendar, RefreshCw, MapPin, Download, Share2, Copy, Check
} from 'lucide-react';

const CATEGORY_META = {
  gbp_readiness: { label: 'GBP Readiness', icon: MapPin, color: '#0268C4' },
  website_relevance: { label: 'Website Relevance', icon: Globe, color: '#22C55E' },
  technical_seo: { label: 'Technical SEO', icon: Zap, color: '#DDA73D' },
  prominence: { label: 'Prominence', icon: Award, color: '#64748B' },
  risk_compliance: { label: 'Risk & Compliance', icon: Shield, color: '#EF4444' }
};

function HealthScore({ score }) {
  const color = score > 80 ? '#22C55E' : score > 50 ? '#DDA73D' : '#EF4444';
  const label = score > 80 ? 'Healthy' : score > 50 ? 'Needs Work' : 'Critical';
  return (
    <div className="flex flex-col items-center">
      <div className="relative w-32 h-32">
        <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="42" fill="none" stroke="#EFF3F8" strokeWidth="8" />
          <circle cx="50" cy="50" r="42" fill="none" stroke={color} strokeWidth="8" strokeLinecap="round"
            strokeDasharray={`${score * 2.64} 264`} className="transition-all duration-1000 ease-out" />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="font-heading text-4xl font-semibold" style={{ color }}>{score}</span>
        </div>
      </div>
      <span className="text-sm font-medium mt-2" style={{ color }}>{label}</span>
      <span className="text-xs text-[#64748B]">Health Score</span>
    </div>
  );
}

function CategoryCard({ category, score }) {
  const meta = CATEGORY_META[category] || { label: category, icon: Globe, color: '#64748B' };
  const Icon = meta.icon;
  const color = score > 80 ? '#22C55E' : score > 50 ? '#DDA73D' : '#EF4444';
  return (
    <div className="bg-white rounded-2xl border border-[#E2E8F0] p-5 shadow-[0_4px_24px_rgba(44,48,46,0.04)]">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4" style={{ color: meta.color }} strokeWidth={1.5} />
        <span className="text-sm font-medium text-[#0F2951]">{meta.label}</span>
      </div>
      <div className="flex items-end gap-2">
        <span className="font-heading text-3xl font-semibold" style={{ color }}>{score}</span>
        <span className="text-sm text-[#64748B] mb-1">/100</span>
      </div>
      <div className="mt-2 h-1.5 bg-[#EFF3F8] rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-700 ease-out" style={{ width: `${score}%`, backgroundColor: color }} />
      </div>
    </div>
  );
}

const EFFORT_COLORS = { S: '#22C55E', M: '#DDA73D', L: '#EF4444' };
const IMPACT_COLORS = { High: '#EF4444', Med: '#DDA73D', Low: '#22C55E' };
const TIMEFRAME_LABELS = { quick_win: 'Quick Win (0-14d)', foundation: 'Foundation (2-6w)', growth: 'Growth (6-12w)' };

export default function AuditDashboardPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [audit, setAudit] = useState(null);
  const [audits, setAudits] = useState([]);
  const [cadence, setCadence] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [tab, setTab] = useState('overview');
  const [shareLoading, setShareLoading] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [pRes, aRes, cRes] = await Promise.all([
          api.get(`/projects/${projectId}`),
          api.get(`/projects/${projectId}/audits`),
          api.get(`/projects/${projectId}/cadence`)
        ]);
        setProject(pRes.data);
        setAudits(aRes.data);
        setCadence(cRes.data);
        if (aRes.data.length > 0 && aRes.data[0].status === 'completed') {
          try {
            const auditRes = await api.get(`/audits/${aRes.data[0].id}`);
            setAudit(auditRes.data);
          } catch {}
        }
      } catch (err) {
        console.error('Failed to load project data:', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [projectId]);

  const runAudit = async () => {
    setRunning(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/audits`);
      const auditDetail = await api.get(`/audits/${data.id}`);
      setAudit(auditDetail.data);
      const auditsList = await api.get(`/projects/${projectId}/audits`);
      setAudits(auditsList.data);
      setTab('overview');
    } catch {} finally {
      setRunning(false);
    }
  };

  const downloadPdf = async () => {
    if (!audit?.id) return;
    try {
      const response = await api.get(`/audits/${audit.id}/pdf`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `LocalLuc_Audit_${project?.business_name?.replace(/\s+/g, '_') || 'Report'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch { }
  };

  const handleShare = async () => {
    if (shareLink) {
      navigator.clipboard.writeText(shareLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      return;
    }
    if (!audit?.id) return;
    setShareLoading(true);
    try {
      const { data } = await api.post(`/audits/${audit.id}/share`);
      const link = `${window.location.origin}/report/${data.token}`;
      setShareLink(link);
      navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { }
    finally { setShareLoading(false); }
  };

  if (loading) return <div className="min-h-screen bg-[#F7F9FC]"><Navbar /><div className="flex items-center justify-center py-32"><Loader2 className="w-8 h-8 text-[#0268C4] animate-spin" /></div></div>;
  if (!project) return <div className="min-h-screen bg-[#F7F9FC]"><Navbar /><div className="max-w-3xl mx-auto px-4 py-12 text-center text-[#64748B]">Project not found. <Link to="/dashboard" className="text-[#0268C4] underline">Go to Dashboard</Link></div></div>;

  const issues = audit?.issues || [];
  const recommendations = audit?.recommendations || [];
  const highImpact = issues.filter(i => i.impact === 'High');
  const categoryScores = audit?.category_scores || {};

  return (
    <div className="min-h-screen bg-[#F7F9FC]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 data-testid="project-heading" className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight text-[#0F2951]">{project?.business_name}</h1>
            <p className="text-sm text-[#64748B] mt-0.5">{project?.business_type} &middot; {project?.city}{project?.state ? `, ${project.state}` : ''}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="edit-goals-button" variant="outline" onClick={() => navigate(`/projects/${projectId}/goals`)} className="border-[#E2E8F0] text-[#64748B] hover:bg-[#EFF3F8] rounded-xl h-10">
              <Target className="w-4 h-4 mr-1.5" /> Goals
            </Button>
            <Button data-testid="view-history-button" variant="outline" onClick={() => navigate(`/projects/${projectId}/history`)} className="border-[#E2E8F0] text-[#64748B] hover:bg-[#EFF3F8] rounded-xl h-10">
              <History className="w-4 h-4 mr-1.5" /> History
            </Button>
            {audit && (
              <Button data-testid="download-pdf-button" variant="outline" onClick={downloadPdf} className="border-[#E2E8F0] text-[#64748B] hover:bg-[#EFF3F8] rounded-xl h-10">
                <Download className="w-4 h-4 mr-1.5" /> PDF
              </Button>
            )}
            {audit && (
              <Button
                data-testid="share-score-button"
                variant="outline"
                onClick={handleShare}
                disabled={shareLoading}
                className="border-[#E2E8F0] text-[#64748B] hover:bg-[#EFF3F8] rounded-xl h-10"
              >
                {shareLoading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : copied ? <Check className="w-4 h-4 mr-1.5 text-[#22C55E]" /> : <Share2 className="w-4 h-4 mr-1.5" />}
                {copied ? 'Copied!' : 'Share'}
              </Button>
            )}
            <Button
              data-testid="run-audit-button"
              onClick={runAudit}
              disabled={running}
              className="bg-[#0268C4] hover:bg-[#015BA8] text-white rounded-xl px-5 h-10 transition-all duration-200 hover:-translate-y-[1px]"
            >
              {running ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Running Audit...</> : <><Play className="w-4 h-4 mr-1.5" /> Run Audit</>}
            </Button>
          </div>
        </div>

        {/* Share Link Banner */}
        {shareLink && (
          <div data-testid="share-link-banner" className="mb-6 bg-[#0268C4]/5 border border-[#0268C4]/20 rounded-2xl p-4 flex items-center gap-3">
            <Share2 className="w-5 h-5 text-[#0268C4] flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[#0F2951]">Share link ready — send it to your partners or team</p>
              <p className="text-xs text-[#64748B] truncate mt-0.5">{shareLink}</p>
            </div>
            <Button
              data-testid="share-banner-copy"
              size="sm"
              onClick={() => { navigator.clipboard.writeText(shareLink); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
              className="bg-[#0268C4] hover:bg-[#015BA8] text-white rounded-xl px-3 h-8 text-xs flex-shrink-0"
            >
              {copied ? <><Check className="w-3 h-3 mr-1" /> Copied</> : <><Copy className="w-3 h-3 mr-1" /> Copy</>}
            </Button>
          </div>
        )}

        {!audit ? (
          /* No audit yet */
          <div className="text-center py-16 bg-white rounded-2xl border border-[#E2E8F0]">
            <div className="w-20 h-20 bg-[#EFF3F8] rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Play className="w-10 h-10 text-[#0268C4]" strokeWidth={1.5} />
            </div>
            <h2 className="font-heading text-xl font-medium text-[#0F2951] mb-2">Ready to audit</h2>
            <p className="text-[#64748B] mb-6 max-w-md mx-auto">Run your first audit to get a complete SEO health check with prioritized action items.</p>
            <Button data-testid="first-audit-button" onClick={runAudit} disabled={running} className="bg-[#0268C4] hover:bg-[#015BA8] text-white rounded-xl px-6 h-11">
              {running ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Running...</> : <><Play className="w-4 h-4 mr-1.5" /> Run First Audit</>}
            </Button>
          </div>
        ) : (
          <>
            {/* Tabs */}
            <Tabs value={tab} onValueChange={setTab} className="space-y-6">
              <TabsList className="bg-white border border-[#E2E8F0] rounded-xl p-1 h-auto">
                <TabsTrigger data-testid="tab-overview" value="overview" className="rounded-lg data-[state=active]:bg-[#0268C4] data-[state=active]:text-white px-4 py-2 text-sm">Overview</TabsTrigger>
                <TabsTrigger data-testid="tab-issues" value="issues" className="rounded-lg data-[state=active]:bg-[#0268C4] data-[state=active]:text-white px-4 py-2 text-sm">All Issues ({issues.length})</TabsTrigger>
                <TabsTrigger data-testid="tab-actions" value="actions" className="rounded-lg data-[state=active]:bg-[#0268C4] data-[state=active]:text-white px-4 py-2 text-sm">Action Plan ({recommendations.length})</TabsTrigger>
                <TabsTrigger data-testid="tab-cadence" value="cadence" className="rounded-lg data-[state=active]:bg-[#0268C4] data-[state=active]:text-white px-4 py-2 text-sm">Cadence</TabsTrigger>
              </TabsList>

              {/* OVERVIEW TAB */}
              <TabsContent value="overview" className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                  {/* Health Score */}
                  <div className="lg:col-span-1 bg-white rounded-2xl border border-[#E2E8F0] p-6 shadow-[0_4px_24px_rgba(44,48,46,0.04)] flex flex-col items-center justify-center">
                    <HealthScore score={audit.overall_score} />
                    <p className="text-xs text-[#64748B] mt-3 text-center">{audit.page_count || 0} pages crawled</p>
                  </div>
                  {/* Category Scores */}
                  <div className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                    {Object.entries(categoryScores).map(([cat, score]) => (
                      <CategoryCard key={cat} category={cat} score={score} />
                    ))}
                  </div>
                </div>

                {/* Top Moves */}
                {recommendations.length > 0 && (
                  <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6 shadow-[0_4px_24px_rgba(44,48,46,0.04)]">
                    <h2 className="font-heading text-lg font-medium text-[#0F2951] mb-4 flex items-center gap-2">
                      <Zap className="w-5 h-5 text-[#0268C4]" strokeWidth={1.5} /> Top Moves
                    </h2>
                    <div className="space-y-3">
                      {recommendations.slice(0, 5).map((r, i) => (
                        <div key={r.id || i} data-testid={`top-move-${i}`} className="flex items-start gap-4 p-4 bg-[#F7F9FC] rounded-xl">
                          <div className="w-7 h-7 bg-[#0268C4] rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0">{i + 1}</div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-[#0F2951]">{r.title}</p>
                            <p className="text-xs text-[#64748B] mt-0.5 line-clamp-2">{r.description}</p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <Badge variant="outline" className="text-xs rounded-lg border-[#E2E8F0]" style={{ color: IMPACT_COLORS[r.impact] }}>{r.impact} Impact</Badge>
                            <Badge variant="outline" className="text-xs rounded-lg border-[#E2E8F0]" style={{ color: EFFORT_COLORS[r.effort] }}>Effort: {r.effort}</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* High Priority Issues */}
                {highImpact.length > 0 && (
                  <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6 shadow-[0_4px_24px_rgba(44,48,46,0.04)]">
                    <h2 className="font-heading text-lg font-medium text-[#0F2951] mb-4 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-[#EF4444]" strokeWidth={1.5} /> High Priority Issues ({highImpact.length})
                    </h2>
                    <div className="space-y-2">
                      {highImpact.slice(0, 5).map((issue, i) => (
                        <Link
                          key={issue.id}
                          to={`/issues/${issue.id}`}
                          data-testid={`high-issue-${i}`}
                          className="flex items-center gap-3 p-3 rounded-xl hover:bg-[#F7F9FC] transition-colors group"
                        >
                          <div className="w-2 h-2 rounded-full bg-[#EF4444] flex-shrink-0" />
                          <span className="text-sm text-[#0F2951] flex-1">{issue.title}</span>
                          <Badge variant="outline" className="text-xs rounded-lg text-[#64748B] border-[#E2E8F0]">
                            {CATEGORY_META[issue.category]?.label || issue.category}
                          </Badge>
                          <ChevronRight className="w-4 h-4 text-[#64748B] group-hover:text-[#0268C4]" />
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </TabsContent>

              {/* ISSUES TAB */}
              <TabsContent value="issues" className="space-y-3">
                {Object.keys(CATEGORY_META).map(cat => {
                  const catIssues = issues.filter(i => i.category === cat);
                  if (catIssues.length === 0) return null;
                  const meta = CATEGORY_META[cat];
                  const Icon = meta.icon;
                  return (
                    <div key={cat} className="bg-white rounded-2xl border border-[#E2E8F0] p-6 shadow-[0_4px_24px_rgba(44,48,46,0.04)]">
                      <h3 className="font-heading text-base font-medium text-[#0F2951] mb-3 flex items-center gap-2">
                        <Icon className="w-4 h-4" style={{ color: meta.color }} /> {meta.label} ({catIssues.length})
                      </h3>
                      <div className="space-y-2">
                        {catIssues.map((issue, i) => (
                          <Link
                            key={issue.id}
                            to={`/issues/${issue.id}`}
                            data-testid={`issue-${cat}-${i}`}
                            className="flex items-center gap-3 p-3 rounded-xl hover:bg-[#F7F9FC] transition-colors group"
                          >
                            <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: IMPACT_COLORS[issue.impact] }} />
                            <span className="text-sm text-[#0F2951] flex-1">{issue.title}</span>
                            <Badge variant="outline" className="text-xs rounded-lg border-[#E2E8F0]" style={{ color: EFFORT_COLORS[issue.effort] }}>Effort: {issue.effort}</Badge>
                            <Badge variant="outline" className="text-xs rounded-lg border-[#E2E8F0]" style={{ color: IMPACT_COLORS[issue.impact] }}>{issue.impact}</Badge>
                            <ChevronRight className="w-4 h-4 text-[#64748B] group-hover:text-[#0268C4]" />
                          </Link>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </TabsContent>

              {/* ACTIONS TAB */}
              <TabsContent value="actions" className="space-y-4">
                {['quick_win', 'foundation', 'growth'].map(tf => {
                  const items = recommendations.filter(r => r.timeframe === tf);
                  if (items.length === 0) return null;
                  return (
                    <div key={tf} className="bg-white rounded-2xl border border-[#E2E8F0] p-6 shadow-[0_4px_24px_rgba(44,48,46,0.04)]">
                      <h3 className="font-heading text-base font-medium text-[#0F2951] mb-4">{TIMEFRAME_LABELS[tf] || tf}</h3>
                      <div className="space-y-3">
                        {items.map((r, i) => (
                          <div key={r.id || i} data-testid={`action-${tf}-${i}`} className="flex items-start gap-4 p-4 bg-[#F7F9FC] rounded-xl">
                            <CheckCircle className="w-5 h-5 text-[#22C55E] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-[#0F2951]">{r.title}</p>
                              <p className="text-xs text-[#64748B] mt-1">{r.description}</p>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <Badge variant="outline" className="text-xs rounded-lg border-[#E2E8F0]" style={{ color: IMPACT_COLORS[r.impact] }}>{r.impact}</Badge>
                              <Badge variant="outline" className="text-xs rounded-lg border-[#E2E8F0]" style={{ color: EFFORT_COLORS[r.effort] }}>{r.effort}</Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
                {recommendations.length === 0 && (
                  <div className="text-center py-12 text-[#64748B]">No recommendations yet. Run an audit first.</div>
                )}
              </TabsContent>

              {/* CADENCE TAB */}
              <TabsContent value="cadence" className="space-y-4">
                {['weekly', 'monthly', 'quarterly'].map(freq => {
                  const tasks = cadence.filter(c => c.frequency === freq);
                  if (tasks.length === 0) return null;
                  return (
                    <div key={freq} className="bg-white rounded-2xl border border-[#E2E8F0] p-6 shadow-[0_4px_24px_rgba(44,48,46,0.04)]">
                      <h3 className="font-heading text-base font-medium text-[#0F2951] mb-4 flex items-center gap-2 capitalize">
                        <RefreshCw className="w-4 h-4 text-[#0268C4]" /> {freq} Tasks
                      </h3>
                      <div className="space-y-3">
                        {tasks.map((task, i) => (
                          <div key={i} data-testid={`cadence-${freq}-${i}`} className="flex items-start gap-3 p-3 bg-[#F7F9FC] rounded-xl">
                            <Calendar className="w-4 h-4 text-[#64748B] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                            <div>
                              <p className="text-sm font-medium text-[#0F2951]">{task.title}</p>
                              <p className="text-xs text-[#64748B] mt-0.5">{task.description}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </TabsContent>
            </Tabs>

            {/* Disclaimer */}
            <div className="mt-8 p-4 bg-[#EFF3F8] rounded-xl border border-[#E2E8F0]">
              <p className="text-xs text-[#64748B]">
                <strong>Disclaimer:</strong> Rankings depend on relevance, distance, and prominence. No tool can guarantee specific ranking positions. Google states there is no way to request or pay for better local ranking. Results vary based on competitive landscape and ongoing effort.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
